import { Grid, Typography, Stack, Tooltip, Rating, Box, Pagination } from "@mui/material";
import { ShPaper } from "shared/SharedStyles/ShSurfaces";
import { ShButton } from "shared/SharedStyles/ShInputs";
import { ShAvatar, ShChip } from "shared/SharedStyles/ShDataDisplay";
import { ResumeScoreGauge } from "../Applicants/ApplicantHelpers";
import { renderMatchChip } from "../Applicants/ApplicantsList/ApplicantsListView";
import { getChipColor } from "../TalentDashboard/TalentDashboardConstants";
import VisibilityIcon from '@mui/icons-material/Visibility';
import DescriptionIcon from '@mui/icons-material/Description';
import { useAppDispatch, useAppSelector } from "helpers/hooks";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ShGreen } from "shared/SharedStyles/styleConstants";
import { ISharedApplicantsListProps } from "./ISharedApplicantsModel";
import { useEffect, useState } from "react";
import { markCandidateAsReviewed } from "store/slices/employer/applicants/applicants-list-slice";
import { ApplicantComment } from "../Applicants/ApplicantsList/ApplicantComment";

export const SharedApplicantsList: React.FC<ISharedApplicantsListProps> = ({applicants, handleViewProfile, paginationParams, handlePageChange}) => {

  const { accountInfo } = useAppSelector((state) => state.employer.employerSettings.accInfo);
  const dispatch = useAppDispatch();
  const noApplicants = applicants.length === 0;
  // const noReviewedApplicants = applicants.every((applicant) => 
  //   !applicant.reviewed_by_account_ids?.includes(accountInfo?.id)
  // );
  const [markingStatus, setMarkingStatus] = useState<{ [applicantId: string]: 'idle' | 'pending' | 'done' }>({});
  const [visibleApplicants, setVisibleApplicants] = useState(applicants);
  
  useEffect(() => {
    setVisibleApplicants(applicants);
  }, [applicants]);

  const handleLocalMarking = async (applicantId: number) => {
    setMarkingStatus((prev) => ({ ...prev, [applicantId]: 'pending' }));
    try {
      if (applicantId && accountInfo?.id) {
        await dispatch(
          markCandidateAsReviewed({
            applicantId,
            accountId: accountInfo.id,
          })
        );
      }
  
      setMarkingStatus((prev) => ({ ...prev, [applicantId]: 'done' }));

      setVisibleApplicants((prev) =>
        prev.filter((applicant) => applicant.id !== applicantId)
      );
  
    } catch (err) {
      setMarkingStatus((prev) => ({ ...prev, [applicantId]: 'idle' }));
    }
  };
  

  return (
    <>
      {noApplicants && (
        <Typography variant="body1" color="textSecondary" align="center" mt={3}>
          No Applicants Assigned to You
        </Typography>
      )}

      {!noApplicants && visibleApplicants?.map((applicant) => (
        <Grid item xs={12} sm={6} md={5} lg={4} xl={4} key={`${applicant.id}-${applicant.candidate.id}`}>
          <ShPaper variant="outlined" leftBorderColor={ShGreen} leftBorderWidth="4px">
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={2} alignItems="center">
                  <ShAvatar sx={{ bgcolor: "primary.main", width: 35, height: 35 }}>
                    {applicant.candidate.account.first_name[0]}
                  </ShAvatar>
                  <Typography variant="body1">
                    {applicant.candidate.account.first_name} {applicant.candidate.account.last_name}
                  </Typography>
                </Stack>
                <ShChip
                  size="small"
                  borderRadius="25px"
                  label={
                    applicant.stage.toLowerCase() === "second_interview"
                      ? "Final Interview"
                      : applicant.stage.toUpperCase()
                  }
                  bgColor={getChipColor(applicant.stage)}
                  textColor="white"
                />
              </Stack>
              <Typography variant="body2" color="textSecondary">
                {applicant.candidate.account.email}
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="space-between" alignContent="center">
                <Typography variant="body2" color="textSecondary">
                  <strong>{applicant.employerjob.name}</strong>
                </Typography>
                {applicant.final_match && (
                  <Box maxWidth={100}>{renderMatchChip(applicant.final_match)}</Box>
                )}
              </Stack>
              <ResumeScoreGauge score={applicant.resume_match_score || 0} />
              <Tooltip title={`Rating: ${applicant.rating}`} arrow>
                <Rating
                  name="read-only"
                  value={applicant.rating || 0}
                  readOnly
                  precision={0.5}
                  size="small"
                />
              </Tooltip>
              <Stack direction="row" spacing={1} pt={1} justifyContent="space-between">
                <Stack direction="row" spacing={1}>
                  <ShButton
                    variant="contained"
                    disableElevation
                    size="small"
                    color="primary"
                    onClick={() => handleViewProfile(applicant.employerjob.id, applicant.id, false)}
                    startIcon={<VisibilityIcon />}
                  >
                    View Profile
                  </ShButton>
                  <ShButton variant="contained" disableElevation size="small" color="primary"
                    onClick={() => handleViewProfile(applicant.employerjob_id, applicant.id, true)}
                    startIcon={<DescriptionIcon />}>
                    Resume Preview
                  </ShButton>
                  <Box width="fit-content">
                      <ApplicantComment
                          applicantId={applicant.id}
                          isAddition={true}
                          label="Add Review"
                      />
                  </Box>
                </Stack>

                    {applicant.reviewed_by_account_ids && applicant.reviewed_by_account_ids.includes(accountInfo?.id) ? (
                    <ShChip label={"Candidate Reviewed"} color="success" size="medium" customSize="small" hideBoxShadow textColor='white' icon={<CheckCircleIcon fontSize="small" style={{color: "white"}} />}/>
                    ) : (
                    <ShButton  variant="outlined"  color="success"  size="small" disableElevation startIcon={<CheckCircleOutlineIcon />}
                      onClick={() => handleLocalMarking(applicant.id)}
                      disabled={markingStatus[applicant.id] === 'pending'}>
                        {markingStatus[applicant.id] === 'pending' ? 'Marking...' : 'Mark Candidate as Reviewed'}
                    </ShButton>
                    )}
              </Stack>
            </Stack>
          </ShPaper>
        </Grid>
      ))}

      <Pagination
        count={paginationParams.totalPages || 1}
        page={paginationParams.requestedPageNumber || 1}
        onChange={handlePageChange}
        color="primary"
        sx={{ marginTop: 2 }}
      />
    </>
  );
};
